// Number purchasing (ADR 0051, docs/specs/number-purchasing.md): search, prices, orders,
// emergency addresses, 911 on existing numbers, release and the number ledger. The shapes
// mirror crates/api-types/src/numbers.rs. Money is integer US cents (`*_minor`).

import { queryOptions } from "@tanstack/react-query";
import { api } from "./client";
import type { PhoneNumber } from "./types";

export type NumberKind = "local" | "toll_free";
/** `kite`: managed, Kite's price book and allowance. `carrier`: own keys, Telnyx bills. */
export type NumberBiller = "kite" | "carrier";

export interface NumberPrice {
  biller: NumberBiller;
  included: boolean;
  monthly_minor: number;
  upfront_minor: number;
  /** Telnyx's 911 fee: own keys, local numbers only. */
  e911_monthly_minor: number;
  /** Sent back as `expected_monthly_minor`. */
  total_monthly_minor: number;
  price_version: string;
}

export interface AvailableNumber {
  e164: string;
  locality: string | null;
  region: string | null;
  texting: boolean;
  quickship: boolean;
  price: NumberPrice;
}

export interface AvailableNumbers {
  numbers: AvailableNumber[];
  /** Toll-free with no 911-active local number: nothing was searched. */
  needs_local_911: boolean;
}

export interface NumberAllowance {
  included: number;
  used: number;
}

export interface PurchaseSettings {
  purchase_enabled: boolean;
  e911_enabled: boolean;
  biller: NumberBiller;
  not_ready: string | null;
  allowance: NumberAllowance | null;
  prices: {
    price_version: string;
    local_monthly_minor: number;
    toll_free_monthly_minor: number;
    upfront_minor: number;
    /** Telnyx's 911 fee per local number; present only when Telnyx bills the company. */
    e911_monthly_minor?: number;
  };
  has_local_911: boolean;
  needs_911_acknowledgment: boolean;
  notice_version: string;
}

export interface NewNumberOrder {
  e164: string;
  number_type: NumberKind;
  brand_id: string | null;
  owner_user_id: string | null;
  label: string | null;
  texting: boolean;
  emergency_address_id: string | null;
  expected_monthly_minor: number;
  expected_upfront_minor: number;
}

export type OrderStatus =
  | "buying"
  | "setting_up"
  | "ready"
  | "needs_attention"
  | "failed"
  | "expired"
  | "cancelled";

export type StepState = "pending" | "done" | "failed" | "skipped";

/** The order dialog's four steps, as far as the server's rows tell. */
export interface OrderSteps {
  bought: StepState;
  calling: StepState;
  e911: StepState;
  texting: StepState;
}

export interface NumberOrder {
  id: string;
  steps: OrderSteps;
  e164: string;
  number_type: NumberKind;
  brand_id: string | null;
  owner_user_id: string | null;
  label: string;
  texting: boolean;
  emergency_address_id: string | null;
  biller: NumberBiller;
  included: boolean;
  price_monthly_minor: number;
  price_upfront_minor: number;
  price_version: string;
  state: string;
  status: OrderStatus;
  reference: string;
  phone_number_id: string | null;
  error_code: string | null;
  can_cancel: boolean;
  created_at: string;
  updated_at: string;
}

export interface AddressInput {
  street: string;
  unit: string | null;
  city: string;
  state: string;
  zip: string;
}

export interface AddressValidation {
  valid: boolean;
  suggested: AddressInput | null;
  errors: string[];
}

export interface NewEmergencyAddress extends AddressInput {
  label: string | null;
  acknowledged: boolean;
}

export interface EmergencyAddress {
  id: string;
  label: string;
  street: string;
  unit: string | null;
  city: string;
  state: string;
  zip: string;
  country: string;
  registration: "draft" | "registering" | "registered" | "failed";
  error_code: string | null;
  in_use: boolean;
  created_at: string;
}

export interface NumberLedgerEntry {
  kind: "charge" | "release" | "adjustment" | string;
  e164: string;
  included: boolean;
  biller: NumberBiller;
  amount_minor: number;
}

export interface NumberLedger {
  month: string;
  entries: NumberLedgerEntry[];
}

const id = encodeURIComponent;

export const numbersApi = {
  settings: () => api<PurchaseSettings>("/v1/admin/numbers/purchase-settings"),
  available: (type: NumberKind, q: string, signal?: AbortSignal) =>
    api<AvailableNumbers>("/v1/admin/numbers/available", { query: { type, q, limit: 20 }, signal }),
  orders: () => api<{ orders: NumberOrder[] }>("/v1/admin/numbers/orders"),
  order: (orderId: string) => api<NumberOrder>(`/v1/admin/numbers/orders/${id(orderId)}`),
  /** One `Idempotency-Key` per review screen, the same on every retry. */
  createOrder: (body: NewNumberOrder, key: string) =>
    api<NumberOrder>("/v1/admin/numbers/orders", {
      method: "POST",
      body,
      headers: { "idempotency-key": key },
    }),
  cancelOrder: (orderId: string) =>
    api<NumberOrder>(`/v1/admin/numbers/orders/${id(orderId)}/cancel`, { method: "POST" }),
  addresses: () => api<{ addresses: EmergencyAddress[] }>("/v1/admin/emergency-addresses"),
  validateAddress: (body: AddressInput) =>
    api<AddressValidation>("/v1/admin/emergency-addresses/validate", { method: "POST", body }),
  createAddress: (body: NewEmergencyAddress) =>
    api<EmergencyAddress>("/v1/admin/emergency-addresses", { method: "POST", body }),
  deleteAddress: (addressId: string) =>
    api<void>(`/v1/admin/emergency-addresses/${id(addressId)}`, { method: "DELETE" }),
  setEmergency: (numberId: string, addressId: string) =>
    api<PhoneNumber>(`/v1/admin/numbers/${id(numberId)}/emergency`, {
      method: "PUT",
      body: { address_id: addressId },
    }),
  /** One `Idempotency-Key` per release dialog. */
  release: (numberId: string, last4: string, key: string) =>
    api<PhoneNumber>(`/v1/admin/numbers/${id(numberId)}/release`, {
      method: "POST",
      body: { last4 },
      headers: { "idempotency-key": key },
    }),
  ledger: (month?: string) =>
    api<NumberLedger>("/v1/admin/numbers/ledger", { query: month ? { month } : {} }),
};

export const nk = {
  settings: ["admin", "numbers", "purchase-settings"] as const,
  orders: ["admin", "numbers", "orders"] as const,
  order: (orderId: string) => ["admin", "numbers", "orders", orderId] as const,
  addresses: ["admin", "numbers", "addresses"] as const,
  available: (type: NumberKind, q: string) => ["admin", "numbers", "available", type, q] as const,
  ledger: ["admin", "numbers", "ledger"] as const,
};

/** Open orders: their rows sit at the top of the Numbers table. */
export const OPEN_STATUSES: readonly OrderStatus[] = ["buying", "setting_up", "needs_attention"];

/** Orders still moving are polled so their rows follow the job. */
const MOVING: readonly OrderStatus[] = ["buying", "setting_up"];

export const purchaseSettingsQuery = queryOptions({ queryKey: nk.settings, queryFn: numbersApi.settings });

export const ordersQuery = queryOptions({
  queryKey: nk.orders,
  queryFn: async () => (await numbersApi.orders()).orders,
  refetchInterval: (q) => (q.state.data?.some((o) => MOVING.includes(o.status)) ? 4_000 : false),
});

export const orderQuery = (orderId: string) =>
  queryOptions({
    queryKey: nk.order(orderId),
    queryFn: () => numbersApi.order(orderId),
    refetchInterval: (q) => (q.state.data && MOVING.includes(q.state.data.status) ? 3_000 : false),
  });

export const addressesQuery = queryOptions({
  queryKey: nk.addresses,
  queryFn: async () => (await numbersApi.addresses()).addresses,
});

export const ledgerQuery = queryOptions({ queryKey: nk.ledger, queryFn: () => numbersApi.ledger() });
